import {Box} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import type {TaxRate} from "crqlar-react-common/dist/models/rate/taxRate.model";
import {TaxRateService} from "crqlar-react-common/dist/services/rate/taxRate.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const TaxRatesList = () => {
    useTrackScreenVisit('system tax rates');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<TaxRate>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: taxRate => taxRate.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 280,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'rate',
            header: t('Rate'),
            accessorFn: taxRate => taxRate.rate,
            cell: ({row}) => <Box>{`${row.original.rate}%`}</Box>,
            size: 140,
            meta: {defaultActive: true}
        },
        {
            id: 'inclusive',
            header: t('Inclusive'),
            accessorFn: taxRate => taxRate.inclusive,
            cell: ({row}) => <Box>{row.original.inclusive ? t('Yes') : t('No')}</Box>,
            size: 140,
            meta: {defaultActive: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: taxRate => taxRate.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: taxRate => taxRate.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-tax-rates',
        columns,
        getRowId: taxRate => taxRate.id!,
        onRowClick: taxRate => navigate(`/system/taxRates/${taxRate.id}`),
        initialSorting: [{id: 'name', desc: false}],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await TaxRateService.getAllTaxRates({
                name: searchQuery ? [searchQuery] : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });
            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    return (
        <SettingsListPage title={t('Tax rates')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create Tax Rate')}
                            onClick={() => navigate('/system/taxRates/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default TaxRatesList;
